/**
 * SOP compiler — turns natural-language SOP body into a structured rule AST.
 *
 * Two paths:
 *   1. Live LLM compile via the existing aiService provider adapter. Returns
 *      a parsed SopRule plus the model used.
 *   2. Heuristic fallback that handles the common shapes ("must be > X",
 *      "must equal Y", "must be present", "must end before DATE"). Runs when
 *      the provider is the mock or the LLM response fails to parse.
 *
 * The compiler never touches the database. Callers persist via
 * sopService.saveCompiledRule.
 *
 * Also exports a deterministic evaluator the simulator uses to run a
 * compiled SopRule against a payload. The same evaluator will be reused by
 * the runtime engine in a later phase.
 */

import { getAiProvider } from '@/services/aiService'
import type { AiMessage } from '@/types/ppm'
import { generateId } from '@/lib/utils'
import { reportedModelLabel } from '@/lib/aiMessages'
import {
  isPredicate,
  isAll,
  isAny,
  isNot,
  type SopRule,
  type SopPredicate,
  type SopFinding,
  type SopScopeKind,
  type SopOp,
} from '@/types/sop'

// ── Compile ──────────────────────────────────────────────────────────

export interface CompileInput {
  nlBody: string
  scopeKind: SopScopeKind
  scopeTarget: string | null
}

export interface CompileResult {
  rule: SopRule
  model: string
  /** True when the heuristic fallback produced the rule (no LLM call). */
  heuristic: boolean
  /** Optional notes the compiler wants to show the author. */
  notes?: string[]
  /**
   * Raw text the LLM produced, when one was called. Surfaced in the editor's
   * Debug panel so authors can see what the model actually said when it
   * disagrees with the AST. Undefined when the heuristic ran without an LLM
   * call (mock provider).
   */
  rawLlmResponse?: string
  /** Number of LLM attempts made. 0 for mock, 1 for one-shot, 2 for retry. */
  attempts?: number
}

const COMPILER_SYSTEM_PROMPT = `You compile Standard Operating Procedures written in plain English into a JSON rule AST.

Output ONLY a single JSON object — no prose, no markdown fences.

AST GRAMMAR
  Rule = Predicate | { "all": Rule[] } | { "any": Rule[] } | { "not": Rule }
  Predicate = { "field": string, "op": Op, "value"?: unknown, "label"?: string }
  Op = "eq" | "neq" | "gt" | "gte" | "lt" | "lte" | "in" | "not_in"
     | "present" | "absent" | "matches" | "before" | "after"

CONVENTIONS
- "field" is a camelCase dotted path on the entity payload. The user message
  carries the scope. Use payload-realistic names: project.projectManager,
  project.endDate, project.status, risk.score, risk.mitigationPlan,
  contract.value, wbs.description, milestone.dueDate.
- "present" / "absent" take no value.
- "before" / "after" compare ISO date strings.
- "matches" takes a regex string in value.
- "in" / "not_in" take an array in value.
- ALWAYS wrap the whole thing in { "all": [...] } even when there is only one
  predicate — the UI expects a consistent shape.
- Each Predicate must include a short human "label" that paraphrases that
  single requirement from the SOP body.
- Translate negative prose ("do not allow X without Y", "must never X") to
  affirmative predicates ("Y must be present", "X must equal …").

FEW-SHOT EXAMPLES

User:
  Scope kind: entity
  Scope target: projects
  SOP body: Project endDate must be present.

You:
  { "all": [ { "field": "endDate", "op": "present", "label": "End date must be present" } ] }

User:
  Scope kind: entity
  Scope target: projects
  SOP body: Do not allow a project to be saved without specifying the project manager.

You:
  { "all": [ { "field": "projectManager", "op": "present", "label": "Project Manager must be set on save" } ] }

User:
  Scope kind: field
  Scope target: projects.roi
  SOP body: New projects must clear an ROI of at least 15%.

You:
  { "all": [ { "field": "roi", "op": "gte", "value": 0.15, "label": "ROI must be at least 15%" } ] }

User:
  Scope kind: entity
  Scope target: risks
  SOP body: A risk scored above 15 must carry a mitigation plan.

You:
  {
    "all": [
      { "field": "score", "op": "lte", "value": 15, "label": "Risk score must be 15 or lower" },
      { "field": "mitigationPlan", "op": "present", "label": "Mitigation plan must be present" }
    ]
  }

IF THE SOP IS GENUINELY UNCOMPILABLE
Return: { "all": [], "_unparseable": "<one-line reason>" }
Use this only when the text names no concrete field or condition — not just
because the phrasing is negative.
`

function buildUserMessage(input: CompileInput, retryHint?: string): string {
  const lines = [
    `Scope kind: ${input.scopeKind}`,
    `Scope target: ${input.scopeTarget ?? '(none)'}`,
    '',
    'SOP body:',
    input.nlBody.trim(),
  ]
  if (retryHint) {
    lines.push('', `Previous attempt failed validation: ${retryHint}. Try again — output ONLY the JSON.`)
  }
  return lines.join('\n')
}

export async function compileSop(input: CompileInput): Promise<CompileResult> {
  const provider = getAiProvider()

  // Mock provider doesn't really compile — go straight to heuristic so the
  // demo experience is honest.
  if (provider.name === 'mock') {
    const heuristicRule = heuristicCompile(input)
    return { rule: heuristicRule, model: 'heuristic', heuristic: true, attempts: 0 }
  }

  const baseMessages = (retryHint?: string): AiMessage[] => [
    {
      id: generateId(),
      role: 'system',
      content: COMPILER_SYSTEM_PROMPT,
      timestamp: new Date().toISOString(),
    },
    {
      id: generateId(),
      role: 'user',
      content: buildUserMessage(input, retryHint),
      timestamp: new Date().toISOString(),
    },
  ]

  let lastRaw = ''
  let attempts = 0
  try {
    // First attempt.
    attempts += 1
    const response1 = await provider.sendMessage(baseMessages())
    lastRaw = response1.content
    const parsed1 = extractJson(response1.content)
    if (parsed1 && isValidRule(parsed1)) {
      return { rule: parsed1, model: reportedModelLabel(provider.lastReportedModel), heuristic: false, rawLlmResponse: lastRaw, attempts }
    }

    // One retry with a hint about what the validator rejected. Many compile
    // failures are recoverable in one shot (missing label, wrong op casing).
    const reason = parsed1 ? 'JSON parsed but did not match the AST schema' : 'response did not contain a JSON object'
    attempts += 1
    const response2 = await provider.sendMessage(baseMessages(reason))
    lastRaw = response2.content
    const parsed2 = extractJson(response2.content)
    if (parsed2 && isValidRule(parsed2)) {
      return {
        rule: parsed2,
        model: reportedModelLabel(provider.lastReportedModel),
        heuristic: false,
        rawLlmResponse: lastRaw,
        attempts,
        notes: [`LLM compile recovered after one retry (${reason}).`],
      }
    }

    // Two consecutive failures — fall back to heuristic and surface what
    // happened so the author can see it.
    return {
      rule: heuristicCompile(input),
      model: 'heuristic',
      heuristic: true,
      attempts,
      rawLlmResponse: lastRaw,
      notes: [`LLM returned unparseable output after ${attempts} attempts; used heuristic compile instead.`],
    }
  } catch (err) {
    return {
      rule: heuristicCompile(input),
      model: 'heuristic',
      heuristic: true,
      attempts,
      rawLlmResponse: lastRaw || undefined,
      notes: [`Compile call failed (${err instanceof Error ? err.message : String(err)}); used heuristic.`],
    }
  }
}

// ── Heuristic fallback ───────────────────────────────────────────────

/**
 * Cheap regex-based parser. Handles the SOP shapes we expect to see most
 * often. Anything more complex returns an empty `all` so the author sees
 * "the compiler couldn't read this — try simpler phrasing or wire the LLM".
 */
export function heuristicCompile(input: CompileInput): SopRule {
  const lines = input.nlBody
    .split(/\n|(?:\.\s+)/)
    .map((s) => s.trim())
    .filter(Boolean)

  const baseField = scopeFieldBase(input.scopeKind, input.scopeTarget)
  const predicates: SopPredicate[] = []

  for (const line of lines) {
    const pred = lineToPredicate(line, baseField)
    if (pred) predicates.push(pred)
  }

  return { all: predicates }
}

function scopeFieldBase(kind: SopScopeKind, target: string | null): string {
  if (!target) return ''
  if (kind === 'field') {
    // 'projects.roi' -> 'roi' (the rule is field-scoped, so the target is
    // already the field; we still expose it as a single-segment path).
    const parts = target.split('.')
    return parts[parts.length - 1] ?? target
  }
  // entity / event scope -> use the target as the entity name root.
  return ''
}

const NUMBER_RE = /-?\d+(?:\.\d+)?/

function groupField(m: RegExpMatchArray | null, baseField: string): string | null {
  const f = m?.groups?.field
  return f ? pickField(f, baseField) : null
}

function lineToPredicate(line: string, baseField: string): SopPredicate | null {
  const lower = line.toLowerCase()

  // "<field> must be present" / "must not be empty"
  const presence = lower.match(/^(?:the\s+)?(?<field>[\w.]+)\s+(?:must|should|has to|needs to)\s+(?:be\s+)?(?:present|set|provided|not\s+empty|not\s+blank|filled in)/i)
  const presField = groupField(presence, baseField)
  if (presField) {
    return { field: presField, op: 'present', label: line }
  }

  // "<field> must be greater than 5" / "> 5"
  const greater = lower.match(/(?<field>[\w.]+)\s+(?:must|should)?\s*(?:be\s+)?(?:greater than|more than|over|above|exceed(?:s|ing)?|>)\s*(?<num>-?\d+(?:\.\d+)?)/i)
  const gField = groupField(greater, baseField)
  if (gField && greater?.groups?.num) {
    return { field: gField, op: 'gt', value: Number(greater.groups.num), label: line }
  }

  // "<field> must be at least 5"
  const atLeast = lower.match(/(?<field>[\w.]+)\s+(?:must|should)?\s*(?:be\s+)?(?:at\s+least|>=|no less than|minimum of)\s*(?<num>-?\d+(?:\.\d+)?)/i)
  const aField = groupField(atLeast, baseField)
  if (aField && atLeast?.groups?.num) {
    return { field: aField, op: 'gte', value: Number(atLeast.groups.num), label: line }
  }

  // "<field> must be less than 5"
  const lessThan = lower.match(/(?<field>[\w.]+)\s+(?:must|should)?\s*(?:be\s+)?(?:less than|under|below|<)\s*(?<num>-?\d+(?:\.\d+)?)/i)
  const lField = groupField(lessThan, baseField)
  if (lField && lessThan?.groups?.num) {
    return { field: lField, op: 'lt', value: Number(lessThan.groups.num), label: line }
  }

  // "<field> equals X" / "must be X"
  const equals = line.match(/(?<field>[\w.]+)\s+(?:must|should)?\s*(?:be\s+|equal\s+)(?:to\s+)?["']?(?<val>[\w-]+)["']?/i)
  const eField = groupField(equals, baseField)
  const eVal = equals?.groups?.val
  if (eField && eVal && !NUMBER_RE.test(eVal)) {
    return { field: eField, op: 'eq', value: eVal, label: line }
  }

  return null
}

function pickField(parsed: string, base: string): string {
  if (parsed.includes('.')) return parsed
  if (base) return base
  return parsed
}

// ── Validation ───────────────────────────────────────────────────────

export function isValidRule(value: unknown): value is SopRule {
  if (!value || typeof value !== 'object') return false
  const v = value as Record<string, unknown>
  if (typeof v.field === 'string' && typeof v.op === 'string') {
    const ops: SopOp[] = ['eq','neq','gt','gte','lt','lte','in','not_in','present','absent','matches','before','after']
    return ops.includes(v.op as SopOp)
  }
  if (Array.isArray(v.all)) return v.all.every((r) => isValidRule(r))
  if (Array.isArray(v.any)) return v.any.every((r) => isValidRule(r))
  if (v.not && typeof v.not === 'object') return isValidRule(v.not)
  return false
}

function extractJson(text: string): unknown {
  // The system prompt asks for raw JSON, but models love wrapping in
  // ```json … ```. Strip fences before parsing.
  const fenced = text.match(/```(?:json)?\s*([\s\S]+?)```/)
  const body = fenced?.[1] ?? text
  try {
    return JSON.parse(body.trim())
  } catch {
    return null
  }
}

// ── Evaluator ────────────────────────────────────────────────────────

/**
 * Run a compiled rule against a payload. Returns the list of clauses that
 * failed. An empty array means the SOP passed.
 *
 * The simulator uses this directly. The runtime engine (later phase) will
 * call it from mutation hooks.
 */
export function evaluateRule(rule: SopRule, payload: Record<string, unknown>): SopFinding[] {
  const findings: SopFinding[] = []
  evalNode(rule, payload, findings)
  return findings
}

function evalNode(rule: SopRule, payload: Record<string, unknown>, findings: SopFinding[]): boolean {
  if (isPredicate(rule)) {
    const ok = evalPredicate(rule, payload)
    if (!ok) {
      findings.push({
        clause: rule.label ?? `${rule.field} ${rule.op}${rule.value === undefined ? '' : ` ${JSON.stringify(rule.value)}`}`,
        message: defaultMessage(rule, payload),
        fieldPath: rule.field,
        expected: rule.value,
        actual: getPath(payload, rule.field),
      })
    }
    return ok
  }
  if (isAll(rule)) {
    let pass = true
    for (const child of rule.all) {
      if (!evalNode(child, payload, findings)) pass = false
    }
    return pass
  }
  if (isAny(rule)) {
    // Collect findings into a scratch list; if anything passes, drop them.
    const scratch: SopFinding[] = []
    const ok = rule.any.some((child) => evalNode(child, payload, scratch))
    if (!ok) findings.push(...scratch)
    return ok
  }
  if (isNot(rule)) {
    const scratch: SopFinding[] = []
    const inner = evalNode(rule.not, payload, scratch)
    if (inner) {
      findings.push({
        clause: 'NOT clause matched',
        message: 'A condition that should not be true was true.',
      })
    }
    return !inner
  }
  return true
}

function evalPredicate(p: SopPredicate, payload: Record<string, unknown>): boolean {
  const actual = getPath(payload, p.field)
  switch (p.op) {
    case 'eq':       return actual === p.value
    case 'neq':      return actual !== p.value
    case 'gt':       return typeof actual === 'number' && typeof p.value === 'number' && actual > p.value
    case 'gte':      return typeof actual === 'number' && typeof p.value === 'number' && actual >= p.value
    case 'lt':       return typeof actual === 'number' && typeof p.value === 'number' && actual < p.value
    case 'lte':      return typeof actual === 'number' && typeof p.value === 'number' && actual <= p.value
    case 'in':       return Array.isArray(p.value) && (p.value as unknown[]).includes(actual)
    case 'not_in':   return Array.isArray(p.value) && !(p.value as unknown[]).includes(actual)
    case 'present':  return actual !== undefined && actual !== null && actual !== ''
    case 'absent':   return actual === undefined || actual === null || actual === ''
    case 'matches':  return typeof actual === 'string' && typeof p.value === 'string' && new RegExp(p.value).test(actual)
    case 'before':   return typeof actual === 'string' && typeof p.value === 'string' && actual < p.value
    case 'after':    return typeof actual === 'string' && typeof p.value === 'string' && actual > p.value
    default:         return false
  }
}

function defaultMessage(p: SopPredicate, payload: Record<string, unknown>): string {
  const actual = getPath(payload, p.field)
  switch (p.op) {
    case 'present': return `${p.field} is required and was empty.`
    case 'absent':  return `${p.field} must be empty but was ${JSON.stringify(actual)}.`
    default:        return `${p.field} expected ${p.op} ${JSON.stringify(p.value)}, got ${JSON.stringify(actual)}.`
  }
}

function getPath(payload: Record<string, unknown>, path: string): unknown {
  if (!path) return undefined
  const parts = path.split('.')
  let cur: unknown = payload
  for (const part of parts) {
    if (cur && typeof cur === 'object' && part in (cur as Record<string, unknown>)) {
      cur = (cur as Record<string, unknown>)[part]
    } else {
      return undefined
    }
  }
  return cur
}
